import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

const BenfitsSection = ({ activeTab = "student" }) => {
  // Memoize the content object to prevent unnecessary recreations
  const content = useMemo(() => ({
    student: [
      "Personalized Lessons Tailored to You",
      "Learn at Your Own Pace",
      "Expert Tutors in Every Subject",
      "Safe & Convenient Home Tuition",
    ],
    tutor: [
      "Flexible Teaching Hours",
      "Earn What You Deserve",
      "Supportive Community",
      "Impact Young Minds",
    ],
  }), []); // Empty dependency array means this only gets created once

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Cycle through content every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(
          (prevIndex) => (prevIndex + 1) % content[activeTab].length
        );
        setIsTransitioning(false);
      }, 500); // Match animation duration
    }, 3000); // Total cycle: 2.5s display + 0.5s transition
    
    return () => clearInterval(interval);
  }, [activeTab, content]); // Now includes content in dependencies

  return (
    <section className="w-full my-20 bg-white py-16 text-black relative">
      {/* Decorative Elements */}
      <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-emerald-50 opacity-30 rounded-full transform translate-x-16 translate-y-16 animate-pulse"></div>
      <div className="absolute top-1/4 left-1/4 w-20 h-20 bg-green-200 opacity-40 rounded-lg rotate-12 hidden md:block"></div>
      <div className="max-w-7xl mx-auto px-8">
        {/* Static Title */}
        <h1 className="text-3xl md:text-5xl 2xl:text-7xl text-gray-600 font-semibold text-center tracking-tight leading-tight mb-12">
          {activeTab === "student"
            ? "Benefits for Students"
            : "Benefits for Tutors"}
        </h1>

        {/* Animated Subtext */}
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="text-center w-56 md:w-[500px] mx-auto">
            <AnimatedText
              className={`text-2xl lg:text-5xl font-semibold text-gray-800 transition-all duration-500 ${
                isTransitioning ? "float-out" : "float-in"
              }`}
            >
              {content[activeTab][currentIndex]}
            </AnimatedText>
          </div>
        </div>
      </div>
    </section>
  );
};

// Styled-component for custom animations
const AnimatedText = styled.p`
  &.float-out {
    animation: floatOut 0.5s ease-in-out forwards;
  }

  &.float-in {
    animation: floatIn 0.5s ease-in-out forwards;
  }

  @keyframes floatOut {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-20px);
    }
  }

  @keyframes floatIn {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export default BenfitsSection;